import { useState } from 'react';
import BaseAssignToolDataTable from '../BaseAssignToolDataTable';
import { GridFilterModel, GridCellParams } from '@mui/x-data-grid-premium';
import {
  useFetchAllPriceAssetGroupsQuery,
  useFetchPriceAssetGroupQuery,
  useCreatePriceAssetGroupMutation,
  useUpdatePriceAssetGroupMutation,
  useDeletePriceAssetGroupsMutation,
} from '../../slices/priceAssetGroupSlice';
import { groupNameValidationSchema } from '../../utils/validation';
import { IconButton } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { PriceAssetGroup } from '../../types/groups';
import { GlobalAssetType, MaxAllowableContent } from '../../types/enums';
import { PriceAsset } from '../../types/priceAssets';
import * as Yup from 'yup';

interface initialValuesShape {
  price_asset_group: PriceAssetGroup;
  members: PriceAsset[];
}

const PriceAssetGroupSection = () => {
  const [deletePriceAssetGroups] = useDeletePriceAssetGroupsMutation();
  const [updatePriceAssetGroup] = useUpdatePriceAssetGroupMutation();
  const [createPriceAssetGroup] = useCreatePriceAssetGroupMutation();
  const [selectedId, setSelectedId] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 24,
  });
  const [_, setQueryOptions] = useState<{
    filterModel?: GridFilterModel;
  }>();

  const initialValues: initialValuesShape = {
    price_asset_group: {
      id: '',
      group_name: '',
      assigned_to_count: 0,
      price_asset_ids: [],
    },
    members: [],
  };
  const { data: selectedIdData = initialValues } = useFetchPriceAssetGroupQuery(
    { priceAssetGroupId: selectedId },
    {
      skip: !selectedId,
    }
  );
  const formatSelectedIdData = (selectedIdData: initialValuesShape) => ({
    id: selectedIdData.price_asset_group.id,
    group_name: selectedIdData.price_asset_group.group_name,
    priceAssets: selectedIdData.members,
  });
  const { data = { total: 0, results: [] }, isFetching } =
    useFetchAllPriceAssetGroupsQuery();

  const getColumns = (handleOpen: (id: string) => void) => [
    {
      field: 'id',
      headerName: 'Edit',
      width: 90,
      filterable: false,
      renderCell: (params: GridCellParams) => {
        return (
          <IconButton
            onClick={() => {
              handleOpen(params.row.id);
            }}
          >
            <OpenInNewIcon />
          </IconButton>
        );
      },
    },
    {
      field: 'group_name',
      flex: 1,
      headerName: 'PriceAsset Group Name',
      editable: true,
    },
    {
      field: 'member_count',
      headerName: '# of members',
      width: 110,
      renderCell: (params: GridCellParams) => {
        return params.row.price_assets.length;
      },
    },
    {
      field: 'assigned_to_count',
      headerName: 'Assigned To',
      width: 90,
    },
  ];

  const validationSchema = Yup.object().shape({
    group_name: groupNameValidationSchema,
  });

  const fieldNames = ['group_name'];
  return (
    <BaseAssignToolDataTable
      data={data}
      isFetching={isFetching}
      assetType={GlobalAssetType.PriceAssetGroup}
      getColumns={getColumns}
      paginationModel={paginationModel}
      setPaginationModel={setPaginationModel}
      setQueryOptions={setQueryOptions}
      updateAsset={async (asset: any) => {
        await updatePriceAssetGroup(asset).unwrap();
      }}
      deleteAssets={deletePriceAssetGroups}
      createAsset={async (asset: any) => {
        await createPriceAssetGroup(asset).unwrap();
      }}
      selectedIdData={formatSelectedIdData(selectedIdData)}
      selectedId={selectedId}
      setSelectedId={setSelectedId}
      validationSchema={validationSchema}
      fieldNames={fieldNames}
      maxAllowableContent={MaxAllowableContent.PriceAssets}
    />
  );
};

export default PriceAssetGroupSection;
